import { Component, Inject, OnInit } from '@angular/core';
import { PaymentGroup } from '../../../../models/entities/payment-group';
import { PaymentGroupsService } from '../../../../services/payment/payment-groups.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JwtLegFiClaims } from 'app/services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from 'app/services/auth/legfi-jwt.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { GrowlerService } from '../../../../services/growler.service';

export interface PaymentDetailDialogInput
{
    paymentGroupId: number;
}

@UntilDestroy()
@Component({
    selector: 'lf-payment-detail-dialog',
    styleUrls: ['./payment-detail-dialog.scss'],
    templateUrl: './payment-detail-dialog.component.html',
})
export class PaymentDetailDialogComponent implements OnInit
{
    public paymentGroup: PaymentGroup = null;
    public fundsAvailable: string = null;
    public jwt: JwtLegFiClaims;
    public totalShort: number = 0;
    public inFlightRequest: boolean = false;

    constructor(
            private _paymentGroupsService: PaymentGroupsService,
            private _growler: GrowlerService,
            private _dialogRef: MatDialogRef<PaymentDetailDialogComponent>,
            private _dialog: MatDialog,
            @Inject(MAT_DIALOG_DATA) private data: PaymentDetailDialogInput,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.loadPaymentGroup();
    }

    /**
     * Issue a credit in the amount of the difference between the payment group's
     * applied value and the actual total of the payment items
     */
    public issueDifferenceCredit() {
        this.inFlightRequest = true;
        this._paymentGroupsService
                .issueCreditForPaymentDifference(null, this.data.paymentGroupId)
                .subscribe(
                        () => {
                            this.inFlightRequest = false;
                            this.loadPaymentGroup();
                        },
                        () => {
                            this.inFlightRequest = false;
                        },
                );
    }

    public openParentPayment(paymentGroupId: number) {
        this._dialogRef.close();
        const data: PaymentDetailDialogInput = {
            paymentGroupId: paymentGroupId,
        };
        this._dialog.open(PaymentDetailDialogComponent, {
            data: data,
        });
    }

    public loadPaymentGroup() {
        this._paymentGroupsService
                .getIncomingPaymentGroup(null, this.data.paymentGroupId)
                .pipe(untilDestroyed(this))
                .subscribe(
                        (paymentGroup: PaymentGroup) => {
                            const offlineRecords = paymentGroup.paymentAttempts.filter(
                                    attempt => {
                                        return attempt.processor === 'offline';
                                    },
                            );
                            if (paymentGroup.paymentAttempts.length > 0 && offlineRecords.length === 0) {
                                const fundsAvailableRecords = paymentGroup.paymentAttempts.filter(
                                        attempt => {
                                            return attempt.fundsAvailable !== null;
                                        },
                                );
                                this.fundsAvailable =
                                        fundsAvailableRecords.length > 0
                                                ? fundsAvailableRecords[0].fundsAvailable
                                                : 'N/A';
                            }

                            this.paymentGroup = paymentGroup;
                            this.paymentGroup.hasValidPayments = paymentGroup.hasValidPayments ||
                                    (paymentGroup.paymentAttempts ? paymentGroup.paymentAttempts.filter((attempt) => {
                                        return !(attempt.deletedAt) && (attempt.success || attempt.pending);
                                    }).length > 0 : false);

                            this.totalShort = paymentGroup.applied;
                            for (const q of paymentGroup.paymentAttempts) {
                                for (const i of q.paymentItems) {
                                    this.totalShort -= i.amount;
                                }
                            }
                        },
                        () => {
                            alert('Unable to load payment information.');
                        },
                );
    }

    public updatePaymentGroupTotals() {
        this._paymentGroupsService.updatePaymentGroupTotals(null, this.data.paymentGroupId).subscribe(
                () => {
                    this._growler.success('Success', 'Payment group totals updated from attempt data.');
                    this.loadPaymentGroup();
                },
                () => {
                    this._growler.oops('There was an error updating this payment group.');
                },
        );
    }

    public restoreCredit(unitId: number, creditId: number) {
        this._paymentGroupsService.restoreUnitAccountCredit(null, unitId, creditId).subscribe(
                () => {
                    this._growler.success('Success', 'Credit successfully restored.');
                    this.loadPaymentGroup();
                },
                () => {
                    this._growler.oops('There was an error restoring this credit.');
                },
        );
    }
}
